<template>
  <div class="event-date-info-view">
    <EventDateInfoHeader
      v-if="selectedDate"
      :date="selectedDate"
      :statuses="selectedFilterValue"
      @onBack="
        () =>
          $router.replace({
            name: ROUTE_EDIT_EVENT,
            params: { productId: $route.params.productId },
          })
      "
      @onEdit="
        () =>
          $router.push({
            name: ROUTE_EVENT_DATE_FORM,
            params: { courseId: selectedDateId },
          })
      "
      @onCancel="handleCancelEventDate"
      @onDelete="handleDeleteEventDate"
    />

    <EventDateInfoBody
      :selected-date-id="selectedDateId"
      :product-id="productId"
      :selected-filter-value="selectedFilterValue"
      :index-of-selected-filter-option="indexOfSelectedFilterOption"
      @onSelectFilter="handleSelectFilter"
      @refreshDate="refreshDate"
    />
  </div>
</template>

<script setup lang="ts">
import EventDateInfoHeader from '@/components/product/product_components/component_events/date_info_components/EventDateInfoHeader.vue'
import EventDateInfoBody from '@/components/product/product_components/component_events/date_info_components/EventDateInfoBody.vue'
import { useDate } from '@/hooks/swr/useDate'
import { onMounted, ref } from 'vue'
import {
  deleteEventDate,
  updateProductEvent,
} from '@/service/v3/endpoints/events'
import { useQueryClient } from '@tanstack/vue-query'
import { QUERY_KEY_EVENT } from '@/queries/events/eventKeys'
import { useRoute } from 'vue-router/composables'
import {
  ROUTE_EDIT_EVENT,
  ROUTE_EVENT_DATE_FORM,
} from '@/router/routes/routeNames'
import { useToast } from '@/hooks/useToast'
import { useI18n } from '@/hooks/useI18N'
import type { RegistrationStatus } from '@/gql/graphql'

const i18n = useI18n()
const { doToast } = useToast()
const { params } = useRoute()
const queryClient = useQueryClient()
const productId = Number(params.productId)
const selectedDateId = Number(params.courseId)
const { unwrappedDate: selectedDate, mutate: refreshDate } =
  useDate(selectedDateId)

// Filter state lives here, in the common parent, so the export button in the
// header can mirror the attendee filter selected in the body.
const selectedFilterValue = ref<RegistrationStatus[] | undefined>(undefined)
const indexOfSelectedFilterOption = ref(0)

const handleSelectFilter = (option: {
  index: number
  value: RegistrationStatus[] | undefined
}) => {
  indexOfSelectedFilterOption.value = option.index
  selectedFilterValue.value = option.value
}

onMounted(() => refreshDate())

const handleDeleteEventDate = async () => {
  const response = await deleteEventDate(selectedDate.value.id)
  if (response.error) {
    doToast({
      text: i18n.t('product_events.toast_messages.DELETE_DATE_ERROR'),
      theme: 'error',
    })
  } else {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_EVENT, productId],
    })
    doToast({
      text: i18n.t('product_events.toast_messages.DELETE_DATE_SUCCESS'),
      theme: 'success',
    })
  }
}

const handleCancelEventDate = async () => {
  const response = await updateProductEvent(productId, selectedDateId, {
    ...selectedDate.value,
    cancelled: true,
  })
  if (response.error) {
    doToast({
      text: i18n.t('product_events.toast_messages.CANCEL_DATE_ERROR'),
      theme: 'error',
    })
  } else {
    refreshDate()
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_EVENT, productId],
    })
    doToast({
      text: i18n.t('product_events.toast_messages.CANCEL_DATE_SUCCESS'),
      theme: 'success',
    })
  }
}
</script>
<style lang="scss" scoped>
.event-date-info-view {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  flex: 1 1 0;
  gap: $spacing-2xs;
}
</style>
